// ============================================
// 镜界客户分布 v4.0 - LinkWave 统一风格
// 支持全屏地图 | 统一布局
// ============================================

import React, { useState, useMemo } from 'react';
import { 
  MapPin, Layers, Search,
  BarChart3, Download,
  Map, List,
  CheckCircle2,
  User
} from 'lucide-react';
import { PageHeader, TabNav, PageContainer, CardGrid, ContentCard, StatCard, ActionButton } from './LayoutComponents';
import AMapComponent from './AMapComponent';

// ==================== 类型定义 ====================

type StoreStatus = 'active' | 'warning' | 'new' | 'inactive';
type StoreType = 'flagship' | 'standard' | 'community';
type ViewMode = 'map' | 'list' | 'stats';

interface StoreClient {
  id: string;
  name: string;
  owner: string;
  phone: string;
  address: string;
  area: string;
  district: string;
  lat: number;
  lng: number;
  status: StoreStatus;
  type: StoreType;
  monthlySales: number;
  totalOrders: number;
  cooperationYears: number;
  brands: string[];
  lastVisit: string;
  nextVisit: string;
  credit: 'A' | 'B' | 'C';
  staffCount: number;
  shopArea: number;
}

interface DistrictStats {
  name: string;
  storeCount: number;
  totalSales: number;
  avgSales: number;
  growth: number;
}

// ==================== 模拟数据 ====================

const MOCK_CLIENTS: StoreClient[] = [
  { id: 'S001', name: '明亮眼镜（国贸店）', owner: '张建国', phone: '138****8888', address: '建国路88号SOHO现代城底商', area: '朝阳区', district: 'CBD', lat: 39.91, lng: 116.47, status: 'active', type: 'flagship', monthlySales: 156000, totalOrders: 45, cooperationYears: 3, brands: ['wx', 'rd'], lastVisit: '2024-03-20', nextVisit: '2024-04-05', credit: 'A', staffCount: 5, shopArea: 120 },
  { id: 'S002', name: '视界眼镜（三里屯店）', owner: '李小红', phone: '139****6666', address: '三里屯太古里南区', area: '朝阳区', district: '三里屯', lat: 39.93, lng: 116.45, status: 'active', type: 'flagship', monthlySales: 128000, totalOrders: 38, cooperationYears: 2, brands: ['wx'], lastVisit: '2024-03-22', nextVisit: '2024-04-08', credit: 'A', staffCount: 4, shopArea: 100 },
  { id: 'S003', name: '新视界（望京店）', owner: '刘先生', phone: '135****5555', address: '望京SOHO T3底商', area: '朝阳区', district: '望京', lat: 39.99, lng: 116.48, status: 'active', type: 'standard', monthlySales: 98000, totalOrders: 32, cooperationYears: 4, brands: ['wx', 'rd'], lastVisit: '2024-03-18', nextVisit: '2024-04-01', credit: 'A', staffCount: 3, shopArea: 80 },
  { id: 'S004', name: '睛彩眼镜（朝阳大悦城店）', owner: '王经理', phone: '137****7777', address: '朝阳大悦城5F', area: '朝阳区', district: '青年路', lat: 39.92, lng: 116.52, status: 'warning', type: 'standard', monthlySales: 45000, totalOrders: 15, cooperationYears: 1, brands: ['wx'], lastVisit: '2024-03-25', nextVisit: '2024-03-28', credit: 'B', staffCount: 2, shopArea: 60 },
  { id: 'S005', name: '光明眼镜（中关村店）', owner: '赵小燕', phone: '136****9999', address: '中关村大街27号中关村大厦', area: '海淀区', district: '中关村', lat: 39.98, lng: 116.31, status: 'active', type: 'flagship', monthlySales: 142000, totalOrders: 41, cooperationYears: 5, brands: ['rd'], lastVisit: '2024-03-19', nextVisit: '2024-04-02', credit: 'A', staffCount: 4, shopArea: 110 },
  { id: 'S006', name: '学子眼镜（五道口店）', owner: '陈老板', phone: '134****4444', address: '成府路五道口购物中心B1', area: '海淀区', district: '五道口', lat: 39.99, lng: 116.33, status: 'active', type: 'standard', monthlySales: 89000, totalOrders: 28, cooperationYears: 2, brands: ['wx'], lastVisit: '2024-03-21', nextVisit: '2024-04-06', credit: 'A', staffCount: 3, shopArea: 70 },
  { id: 'S007', name: '护眼中心（西三旗店）', owner: '孙经理', phone: '133****3333', address: '西三旗建材城西路', area: '海淀区', district: '西三旗', lat: 40.05, lng: 116.35, status: 'new', type: 'community', monthlySales: 32000, totalOrders: 8, cooperationYears: 0, brands: ['wx'], lastVisit: '2024-03-26', nextVisit: '2024-04-10', credit: 'B', staffCount: 2, shopArea: 50 },
  { id: 'S008', name: '通州眼镜城（万达店）', owner: '周老板', phone: '132****2222', address: '通州区万达广场1F', area: '通州区', district: '通州万达', lat: 39.91, lng: 116.66, status: 'active', type: 'standard', monthlySales: 76000, totalOrders: 25, cooperationYears: 3, brands: ['wx'], lastVisit: '2024-03-17', nextVisit: '2024-04-03', credit: 'B', staffCount: 3, shopArea: 75 },
  { id: 'S009', name: '明亮眼镜（梨园店）', owner: '吴店长', phone: '131****1111', address: '通州区梨园南街', area: '通州区', district: '梨园', lat: 39.88, lng: 116.68, status: 'warning', type: 'community', monthlySales: 28000, totalOrders: 10, cooperationYears: 2, brands: ['wx'], lastVisit: '2024-03-24', nextVisit: '2024-03-29', credit: 'C', staffCount: 2, shopArea: 45 },
  { id: 'S010', name: '皇城眼镜（西单店）', owner: '郑总', phone: '130****0000', address: '西单大悦城B1层', area: '西城区', district: '西单', lat: 39.91, lng: 116.37, status: 'active', type: 'flagship', monthlySales: 168000, totalOrders: 52, cooperationYears: 6, brands: ['wx', 'rd'], lastVisit: '2024-03-15', nextVisit: '2024-04-01', credit: 'A', staffCount: 6, shopArea: 130 },
  { id: 'S011', name: '护眼镜（金融街店）', owner: '钱经理', phone: '139****8888', address: '金融街购物中心2F', area: '西城区', district: '金融街', lat: 39.91, lng: 116.35, status: 'active', type: 'standard', monthlySales: 112000, totalOrders: 35, cooperationYears: 3, brands: ['rd'], lastVisit: '2024-03-23', nextVisit: '2024-04-07', credit: 'A', staffCount: 3, shopArea: 85 },
  { id: 'S012', name: '明亮眼镜（东直门店）', owner: '冯老板', phone: '138****7777', address: '东直门外大街48号', area: '东城区', district: '东直门', lat: 39.94, lng: 116.43, status: 'active', type: 'standard', monthlySales: 95000, totalOrders: 30, cooperationYears: 4, brands: ['wx'], lastVisit: '2024-03-20', nextVisit: '2024-04-05', credit: 'A', staffCount: 3, shopArea: 75 },
];

const STATUS_CONFIG: Record<StoreStatus, { label: string; color: string; bg: string; border: string }> = {
  active: { label: '活跃', color: 'text-[#228B22]', bg: 'bg-[rgba(34,139,34,0.1)]', border: 'border-[rgba(34,139,34,0.2)]' },
  warning: { label: '预警', color: 'text-[#EF4444]', bg: 'bg-[rgba(239,68,68,0.1)]', border: 'border-[rgba(239,68,68,0.2)]' },
  new: { label: '新入驻', color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
  inactive: { label: '休眠', color: 'text-gray-600', bg: 'bg-gray-100', border: 'border-gray-200' },
};

const TYPE_CONFIG: Record<StoreType, { label: string; icon: string }> = {
  flagship: { label: '旗舰店', icon: '👑' },
  standard: { label: '标准店', icon: '🏪' },
  community: { label: '社区店', icon: '🏠' },
};

// ==================== 主组件 ====================

export const ClientMapModule: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('map');
  const [filterStatus, setFilterStatus] = useState<StoreStatus | 'all'>('all');
  const [filterArea, setFilterArea] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState<StoreClient | null>(null);
  const [showHeatmap, setShowHeatmap] = useState(false);

  const areas = useMemo(() => [...new Set(MOCK_CLIENTS.map(c => c.area))], []);

  const filteredClients = useMemo(() => {
    return MOCK_CLIENTS.filter(client => {
      if (filterStatus !== 'all' && client.status !== filterStatus) return false;
      if (filterArea !== 'all' && client.area !== filterArea) return false;
      if (searchTerm && !client.name.includes(searchTerm) && !client.owner.includes(searchTerm)) return false;
      return true;
    });
  }, [filterStatus, filterArea, searchTerm]);

  const stats = useMemo(() => {
    const active = filteredClients.filter(c => c.status === 'active').length;
    const warning = filteredClients.filter(c => c.status === 'warning').length;
    const newStores = filteredClients.filter(c => c.status === 'new').length;
    const totalSales = filteredClients.reduce((sum, c) => sum + c.monthlySales, 0);
    const avgSales = filteredClients.length > 0 ? totalSales / filteredClients.length : 0;
    return { total: filteredClients.length, active, warning, newStores, totalSales, avgSales };
  }, [filteredClients]);

  const districtStats: DistrictStats[] = useMemo(() => {
    return areas.map(area => {
      const areaClients = MOCK_CLIENTS.filter(c => c.area === area);
      const totalSales = areaClients.reduce((sum, c) => sum + c.monthlySales, 0);
      return {
        name: area,
        storeCount: areaClients.length,
        totalSales,
        avgSales: areaClients.length > 0 ? totalSales / areaClients.length : 0,
        growth: Math.random() * 20 - 5,
      };
    }).sort((a, b) => b.totalSales - a.totalSales);
  }, [areas]);

  // 状态筛选选项
  const statusOptions = [
    { id: 'all' as const, label: '全部', count: stats.total },
    { id: 'active' as const, label: '活跃', count: stats.active },
    { id: 'warning' as const, label: '预警', count: stats.warning },
    { id: 'new' as const, label: '新入驻', count: stats.newStores },
  ];

  return (
    <PageContainer>
      {/* 头部 */}
      <PageHeader
        icon={MapPin}
        title="客户分布"
        subtitle={`${stats.total}家门店 · 覆盖${areas.length}个区域`}
        extra={
          <div className="flex items-center gap-6">
            <StatCard icon={CheckCircle2} label="活跃门店" value={stats.active} color="green" />
            <StatCard icon={MapPin} label="预警门店" value={stats.warning} color="red" />
            <StatCard icon={User} label="新入驻" value={stats.newStores} color="blue" />
            <div className="w-px h-10 bg-gray-200"></div>
            <div className="text-center">
              <p className="text-2xl font-bold text-[#8B4789]">¥{(stats.totalSales / 10000).toFixed(0)}万</p>
              <p className="text-xs text-gray-500">月销总额</p>
            </div>
          </div>
        }
      />

      {/* Tab 导航 */}
      <TabNav
        tabs={[
          { id: 'map', label: '地图视图', icon: Map },
          { id: 'list', label: '列表视图', icon: List },
          { id: 'stats', label: '统计分析', icon: BarChart3 },
        ]}
        activeTab={viewMode}
        onChange={setViewMode}
      />

      {/* 筛选栏 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <StatusFilter options={statusOptions} active={filterStatus} onChange={setFilterStatus} />
          <select
            value={filterArea}
            onChange={(e) => setFilterArea(e.target.value)}
            className="px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#C41E3A]"
          >
            <option value="all">全部区域</option>
            {areas.map(area => (
              <option key={area} value={area}>{area}</option>
            ))}
          </select>
          {viewMode === 'map' && (
            <ActionButton 
              icon={Layers} 
              variant={showHeatmap ? 'primary' : 'secondary'}
              onClick={() => setShowHeatmap(!showHeatmap)}
            >
              {showHeatmap ? '隐藏热力' : '显示热力'}
            </ActionButton>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="搜索门店/负责人"
              className="w-64 pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#C41E3A] focus:ring-2 focus:ring-[rgba(196,30,58,0.1)] transition-all"
            />
          </div>
          <ActionButton icon={Download} variant="secondary">导出</ActionButton>
        </div>
      </div>

      {/* 内容区域 */}
      {viewMode === 'map' && (
        <MapView 
          clients={filteredClients}
          selectedClient={selectedClient}
          onSelectClient={setSelectedClient}
          showHeatmap={showHeatmap}
          districtStats={districtStats}
        />
      )}

      {viewMode === 'list' && (
        <ListView clients={filteredClients} onSelect={setSelectedClient} />
      )}

      {viewMode === 'stats' && (
        <StatsView districtStats={districtStats} stats={stats} />
      )}
    </PageContainer>
  );
};

// 状态筛选组件
const StatusFilter = <T extends string>({
  options,
  active,
  onChange,
}: {
  options: Array<{ id: T; label: string; count: number }>;
  active: T;
  onChange: (id: T) => void;
}) => (
  <div className="flex bg-white rounded-xl border border-gray-200 p-1 shadow-sm">
    {options.map((opt) => (
      <button
        key={opt.id}
        onClick={() => onChange(opt.id)}
        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
          active === opt.id 
            ? 'bg-gray-900 text-white' 
            : 'text-gray-600 hover:text-gray-900'
        }`}
      >
        {opt.label} ({opt.count})
      </button>
    ))}
  </div>
);

// ==================== 子组件 ====================

// 1. 地图视图
const MapView: React.FC<{
  clients: StoreClient[];
  selectedClient: StoreClient | null;
  onSelectClient: (c: StoreClient | null) => void;
  showHeatmap: boolean;
  districtStats: DistrictStats[];
}> = ({ clients, selectedClient, onSelectClient, showHeatmap }) => {
  const markers = clients.map(client => ({
    position: [client.lng, client.lat] as [number, number],
    title: client.name,
    content: `月销: ¥${(client.monthlySales / 10000).toFixed(1)}万`,
    color: client.status === 'active' ? 'bg-[#228B22]' : client.status === 'warning' ? 'bg-[#EF4444]' : 'bg-blue-500',
    icon: client.name[0],
  }));

  return (
    <CardGrid cols={3} className="h-[600px]">
      {/* 门店列表 */}
      <ContentCard className="h-full overflow-hidden flex flex-col p-0">
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <p className="font-semibold text-gray-900">门店列表 ({clients.length})</p>
        </div>
        <div className="flex-1 overflow-y-auto">
          {clients.map((client) => (
            <button
              key={client.id}
              onClick={() => onSelectClient(client)}
              className={`w-full p-4 border-b border-gray-100 text-left hover:bg-gray-50 transition-colors ${
                selectedClient?.id === client.id ? 'bg-[rgba(196,30,58,0.05)] border-l-4 border-l-[#C41E3A]' : ''
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-lg ${
                    client.status === 'active' ? 'bg-[rgba(34,139,34,0.1)]' :
                    client.status === 'warning' ? 'bg-[rgba(239,68,68,0.1)]' :
                    client.status === 'new' ? 'bg-blue-50' :
                    'bg-gray-100'
                  }`}>
                    {TYPE_CONFIG[client.type].icon}
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 text-sm">{client.name}</h4>
                    <p className="text-xs text-gray-500">{client.area} · {client.district}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${STATUS_CONFIG[client.status].bg} ${STATUS_CONFIG[client.status].color}`}>
                    {STATUS_CONFIG[client.status].label}
                  </span>
                  <p className="text-sm font-bold text-gray-900 mt-1">¥{(client.monthlySales / 10000).toFixed(1)}万</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </ContentCard>

      {/* 地图区域 */}
      <div className="col-span-2 h-full">
        <AMapComponent
          height="100%"
          center={[116.397428, 39.90923]}
          zoom={11}
          markers={markers}
          selectedPoint={selectedClient ? { lng: selectedClient.lng, lat: selectedClient.lat } : null}
          showHeatmap={showHeatmap}
          allowFullscreen
        />
      </div>
    </CardGrid>
  );
};

// 2. 列表视图
const ListView: React.FC<{ clients: StoreClient[]; onSelect: (c: StoreClient) => void }> = ({ clients, onSelect }) => (
  <ContentCard className="p-0 overflow-hidden">
    <table className="w-full">
      <thead className="bg-gray-50 border-b border-gray-200">
        <tr>
          <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase">门店</th>
          <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase">区域</th>
          <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase">月销</th>
          <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase">负责人</th>
          <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase">状态</th>
          <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase">操作</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {clients.map((client) => (
          <tr key={client.id} className="hover:bg-gray-50/50 transition-colors">
            <td className="py-4 px-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl gradient-brand flex items-center justify-center text-white font-bold">
                  {client.name[0]}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{client.name}</p>
                  <p className="text-xs text-gray-500">{TYPE_CONFIG[client.type].label}</p>
                </div>
              </div>
            </td>
            <td className="py-4 px-6">
              <p className="text-sm text-gray-900">{client.area}</p>
              <p className="text-xs text-gray-500">{client.district}</p>
            </td>
            <td className="py-4 px-6">
              <p className="font-semibold text-gray-900">¥{(client.monthlySales / 10000).toFixed(1)}万</p>
              <p className="text-xs text-gray-500">{client.totalOrders}单</p>
            </td>
            <td className="py-4 px-6">
              <p className="text-sm text-gray-900">{client.owner}</p>
              <p className="text-xs text-gray-500">{client.phone}</p>
            </td>
            <td className="py-4 px-6">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${STATUS_CONFIG[client.status].bg} ${STATUS_CONFIG[client.status].color} ${STATUS_CONFIG[client.status].border}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${client.status === 'active' ? 'bg-[#228B22]' : client.status === 'warning' ? 'bg-[#EF4444]' : 'bg-gray-500'}`}></span>
                {STATUS_CONFIG[client.status].label}
              </span>
            </td>
            <td className="py-4 px-6">
              <ActionButton variant="ghost" onClick={() => onSelect(client)}>
                查看详情 →
              </ActionButton>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </ContentCard>
);

// 3. 统计视图
const StatsView: React.FC<{ districtStats: DistrictStats[]; stats: any }> = ({ districtStats, stats }) => (
  <CardGrid cols={3}>
    <ContentCard title="区域销售排行" className="col-span-2">
      <div className="space-y-4">
        {districtStats.map((district, index) => (
          <div key={district.name} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
              index === 0 ? 'bg-yellow-100 text-yellow-700' :
              index === 1 ? 'bg-gray-200 text-gray-700' :
              index === 2 ? 'bg-orange-100 text-orange-700' :
              'bg-gray-100 text-gray-500'
            }`}>
              {index + 1}
            </div>
            <div className="flex-1">
              <p className="font-semibold text-gray-900">{district.name}</p>
              <p className="text-xs text-gray-500">{district.storeCount}家门店</p>
            </div>
            <div className="text-right">
              <p className="font-bold text-gray-900">¥{(district.totalSales / 10000).toFixed(0)}万</p>
              <p className={`text-xs ${district.growth >= 0 ? 'text-[#228B22]' : 'text-[#EF4444]'}`}>
                {district.growth >= 0 ? '+' : ''}{district.growth.toFixed(1)}%
              </p>
            </div>
            <div className="w-24">
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full gradient-brand rounded-full" style={{ width: `${Math.min((district.totalSales / 500000) * 100, 100)}%` }}></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </ContentCard>

    <div className="space-y-5">
      <ContentCard className="bg-gradient-to-br from-[#C41E3A] to-[#8B4789] text-white">
        <h3 className="font-semibold mb-4">整体概况</h3>
        <div className="space-y-4">
          <div>
            <p className="text-sm text-white/80">本月总销售额</p>
            <p className="text-3xl font-bold">¥{(stats.totalSales / 10000).toFixed(0)}万</p>
          </div>
          <div>
            <p className="text-sm text-white/80">平均单店月销</p>
            <p className="text-2xl font-bold">¥{(stats.avgSales / 10000).toFixed(1)}万</p>
          </div>
          <div>
            <p className="text-sm text-white/80">门店总数</p>
            <p className="text-2xl font-bold">{stats.total}家</p>
          </div>
        </div>
      </ContentCard>

      <ContentCard title="状态分布">
        <div className="space-y-3">
          {[
            { label: '活跃门店', value: stats.active, total: stats.total, color: 'bg-[#228B22]' },
            { label: '预警门店', value: stats.warning, total: stats.total, color: 'bg-[#EF4444]' },
            { label: '新入驻', value: stats.newStores, total: stats.total, color: 'bg-blue-500' },
          ].map((item) => (
            <div key={item.label}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-gray-600">{item.label}</span>
                <span className="font-medium">{item.value}家 ({((item.value / item.total) * 100).toFixed(0)}%)</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className={`h-full ${item.color} rounded-full`} style={{ width: `${(item.value / item.total) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      </ContentCard>

      <ActionButton icon={Download} variant="secondary" className="w-full">
        导出分布报告
      </ActionButton>
    </div>
  </CardGrid>
);

export default ClientMapModule;
